import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { postsService } from '@/services/posts.service';
import { postsApi, Post } from '@/services/posts';
import {
  CreatePostData,
  UpdatePostData,
  SchedulePostData,
} from '@/types/posts';
import { teamQueryKeys, PostFilters } from '@/lib/queryKeys';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useTranslation } from 'react-i18next';
import { toast } from '@/utils/toast';

/**
 * React Query hooks for posts
 */

/**
 * Fetch all posts
 */
export const usePosts = (filters?: PostFilters, enabled?: boolean) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.posts.list(teamId, filters),
    queryFn: () => postsService.getPosts(filters),
    enabled: hasTeam && (enabled ?? true),
    staleTime: 60000, // 1 minute
  });
};

/**
 * Fetch a single post
 * Returns the full Post type from services/posts.ts with all fields
 */
export const usePost = (id: number, options?: { enabled?: boolean }) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery<Post, Error>({
    queryKey: teamQueryKeys.posts.detail(teamId, id),
    queryFn: ({ signal }) => postsApi.getById(id, signal),
    enabled: hasTeam && !!id && id > 0 && (options?.enabled ?? true),
  });
};

/**
 * Create a new post
 */
export const useCreatePost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (data: CreatePostData) => postsService.createPost(data),
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Update an existing post
 */
export const useUpdatePost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePostData }) =>
      postsService.updatePost(id, data),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.id });
    },
  });
};

/**
 * Delete a post
 */
export const useDeletePost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => postsService.deletePost(id),
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Schedule a post
 */
export const useSchedulePost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SchedulePostData }) =>
      postsService.schedulePost(id, data),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.id });
    },
  });
};

/**
 * Publish a post immediately
 */
export const usePublishPost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => postsService.publishPost(id),
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

// ============================================================================
// ADDITIONAL POST HOOKS (from posts.ts API)
// ============================================================================

/**
 * Retry a failed post
 */
export const useRetryPost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => postsApi.retry(id),
    onSuccess: (_, id) => {
      invalidateReleaseState(queryClient, { teamId, postId: id });
    },
  });
};

/**
 * Duplicate a post
 */
export const useDuplicatePost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => postsApi.duplicate(id),
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Bulk delete posts
 */
export const useBulkDeletePosts = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (postIds: number[]) => postsApi.bulkDelete(postIds),
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Bulk reschedule posts
 */
export const useBulkReschedulePosts = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  const { t } = useTranslation('posts');

  return useMutation({
    mutationFn: ({ postIds, scheduledFor }: { postIds: number[]; scheduledFor: string }) =>
      postsApi.bulkReschedule(postIds, scheduledFor),
    onSuccess: (result) => {
      invalidateReleaseState(queryClient, { teamId });
      // Conflict-aware summary: never claim full success when posts were skipped.
      const skipped = result.skippedDueToConflict?.length ?? 0;
      if (skipped > 0) {
        toast.warning(
          t('bulk_reschedule_partial', { count: result.rescheduledCount, skipped })
        );
      }
    },
  });
};

/**
 * Get post statistics
 */
export const usePostStats = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.posts.stats(teamId),
    queryFn: postsApi.getStats,
    enabled: hasTeam,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refresh every minute
  });
};
